import React, { useCallback, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import EditProjectModal from "../../components/EditProjectModal/EditProjectModal";
import EditDescriptionModal from "./EditDescriptionModal";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import PublicListingOverview from "../../components/PublicListingOverview/PublicListingOverview";
import ReadOnlyField from "../../components/ReadOnlyField/ReadOnlyField";
import StripeConnectCTA from "../../components/StripeConnectCTA/StripeConnectCTA";
import { TagList } from "../../components/TagList/TagList";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { useProject } from "../../hooks/use-project";
import { usePublicListing } from "../../hooks/use-public-listing";
import { useStripeConnectInfo } from "../../hooks/use-stripe-connect-info";
import { formatTimestampNoTime } from "../../lib/format-timestamp";
import { mapStartupStatusToText } from "../../lib/map-startup-status-to-text";
import { deleteProject } from "../../services/projectService";
import { StartupStatus } from "../../types/StartupStatus";
import "./ProjectPage.scss";

const ProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project, isLoading, error } = useProject(projectId ?? null);
  const { stripeConnectInfo } = useStripeConnectInfo(projectId ?? null);
  const { publicListing } = usePublicListing(projectId ?? null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  useDocumentTitle(project ? `Project: ${project.name}` : "Project Details");

  const handleDeleteProject = useCallback(async () => {
    if (!project) {
      return;
    }

    const rc = window.confirm(
      `Are you sure you want to delete ${project.name}?`,
    );
    if (!rc) {
      return;
    }
    await deleteProject(project.id);
  }, [project]);

  if (isLoading) {
    return <LoadingMask message="Loading project details..." />;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!project || !projectId) {
    return <Alert variant="danger">Project data is unavailable.</Alert>;
  }

  return (
    <div className="project-page">
      <div className="page-header">Project: {project.name}</div>
      <div className="row">
        <div className="col-lg-8">
          <StripeConnectCTA
            project={project}
            stripeConnectInfo={stripeConnectInfo}
          />
        </div>
        <div className="col-lg-4">
          <div className="inner">
            <section className="overview-section">
              <div className="section-header">Overview</div>
              <div className="stack read-only-fields">
                <ReadOnlyField
                  label="Description"
                  value={project.description}
                  onEditClick={() => setShowDescriptionModal(true)}
                />
                <ReadOnlyField
                  label="Status"
                  onEditClick={() => setShowEditModal(true)}
                  value={
                    project.startupStatus &&
                    project.startupStatus.length > 0 ? (
                      <TagList<StartupStatus>
                        tags={project.startupStatus ?? []}
                        mapTagToText={mapStartupStatusToText}
                      />
                    ) : (
                      <div>No listed status</div>
                    )
                  }
                />
                <ReadOnlyField
                  label="Sectors"
                  onEditClick={() => setShowEditModal(true)}
                  value={
                    project.sectors && project.sectors.length > 0 ? (
                      <TagList<string>
                        tags={project.sectors ?? []}
                        mapTagToText={(sector) => sector}
                      />
                    ) : (
                      <div>No listed sectors</div>
                    )
                  }
                />
                <ReadOnlyField
                  label="Created"
                  value={formatTimestampNoTime(project.createdAt)}
                />
                <ReadOnlyField
                  label="Last Updated"
                  value={formatTimestampNoTime(project.updatedAt)}
                />
                <EditProjectModal
                  project={project}
                  show={showEditModal}
                  onHide={() => setShowEditModal(false)}
                  onUpdate={() => {}}
                />
                <EditDescriptionModal
                  projectId={project.id}
                  currentDescription={project.description}
                  show={showDescriptionModal}
                  onHide={() => setShowDescriptionModal(false)}
                />
              </div>
            </section>
            <PublicListingOverview
              projectId={projectId}
              showManageButton
              publicListing={publicListing}
            />
            <section className="sidebar-section">
              <div className="section-header">Admin</div>
              <Button variant="danger" onClick={handleDeleteProject}>
                Delete Project
              </Button>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPage;
